import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { money, timeAgo } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Star, Search, Phone, Mail } from "lucide-react";

export default function Customers() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", email: "", notes: "" });
  const { data: customers = [] } = useQuery({ queryKey: ["customers"], queryFn: async () => (await api.get("/customers")).data });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const sym = settings?.currency_symbol || "₹";

  const add = async () => {
    if (!form.name || !form.phone) return toast.error("Name & phone required");
    await api.post("/customers", form); toast.success("Customer added"); setOpen(false);
    setForm({ name: "", phone: "", email: "", notes: "" });
    qc.invalidateQueries({ queryKey: ["customers"] });
  };

  const list = customers.filter((c) => !search || c.name.toLowerCase().includes(search.toLowerCase()) || (c.phone || "").includes(search));
  const totalPoints = customers.reduce((s, c) => s + (c.loyalty_points || 0), 0);

  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8">
      <PageHeader title="Customers & Loyalty" subtitle={`${customers.length} members · ${totalPoints} points issued`}>
        <Button onClick={() => setOpen(true)} data-testid="add-cust-btn" className="bg-[var(--kul-primary)] hover:bg-[var(--kul-primary-hover)] text-white"><Plus className="w-4 h-4 mr-1" /> Add Customer</Button>
      </PageHeader>

      <div className="relative mb-4 max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or phone…" className="pl-9" data-testid="cust-search" />
      </div>

      <div className="kul-card overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-neutral-50">
            <TableHead>Customer</TableHead><TableHead>Contact</TableHead><TableHead className="text-right">Visits</TableHead>
            <TableHead className="text-right">Total Spent</TableHead><TableHead className="text-right">Loyalty</TableHead><TableHead className="text-right">Rating</TableHead><TableHead>Last Visit</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {list.map((c, idx) => (
              <TableRow key={c.id} className={idx % 2 ? "bg-neutral-50/50" : ""} data-testid={`cust-row-${c.id}`}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell className="text-sm text-neutral-500">
                  <div className="flex items-center gap-1"><Phone className="w-3 h-3" /> {c.phone}</div>
                  {c.email && <div className="flex items-center gap-1 text-xs"><Mail className="w-3 h-3" /> {c.email}</div>}
                </TableCell>
                <TableCell className="text-right font-mono-k">{c.visits || 0}</TableCell>
                <TableCell className="text-right font-mono-k">{money(c.total_spent, sym)}</TableCell>
                <TableCell className="text-right"><Badge className="bg-yellow-100 text-yellow-800 gap-1"><Star className="w-3 h-3" />{c.loyalty_points || 0}</Badge></TableCell>
                <TableCell className="text-right">{c.rating ? <span className="inline-flex items-center gap-0.5 text-sm"><Star className="w-3.5 h-3.5 fill-[#FFCC00] text-[#FFCC00]" />{c.rating}</span> : <span className="text-neutral-300">—</span>}</TableCell>
                <TableCell className="text-sm text-neutral-400">{c.last_visit ? timeAgo(c.last_visit) : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!list.length && <p className="text-neutral-400 text-center py-12">No customers found.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>Add Customer</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cust-name" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="cust-phone" /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Preferences, allergies…" /></div>
          </div>
          <DialogFooter><Button onClick={add} className="bg-[var(--kul-primary)] text-white" data-testid="save-cust">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
